import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Mail, MapPin, Phone, Leaf } from 'lucide-react';
import { BRAND } from '@/lib/brand';
import { PRODUCTS } from '@/data/products';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export const Footer = () => {
  const handleSubscribe = (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    toast.success(`We'll be in touch at ${data.get('email')}`, { description: 'Welcome to the early-morning bakery list.' });
    e.currentTarget.reset();
  };
  return (
    <footer id="contact" className="relative mt-32 bg-primary text-primary-foreground overflow-hidden">
      <div className="absolute inset-0 bg-noise opacity-[0.05] pointer-events-none" />
      <div className="absolute -top-32 -right-24 h-80 w-80 rounded-full bg-accent/20 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-primary-glow/20 blur-3xl" />

      <div className="container mx-auto relative">
        {/* CTA strip */}
        <div className="py-16 border-b border-primary-foreground/15">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="font-script text-2xl text-accent">stay in the proof</div>
              <h3 className="font-display text-4xl sm:text-5xl font-medium leading-tight mt-1">
                A warm pastry,<br/> straight to your inbox.
              </h3>
              <p className="mt-4 text-primary-foreground/70 max-w-md">
                Monthly drops on new products, seasonal menus, campus residencies and the occasional baker's secret.
              </p>
            </div>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
              <Input
                name="email"
                type="email"
                required
                placeholder="you@university.edu"
                className="h-12 rounded-full bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 focus-visible:ring-accent"
              />
              <Button type="submit" className="h-12 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 px-6 font-medium">
                Subscribe
              </Button>
            </form>
          </div>
        </div>

        {/* Columns */}
        <div className="grid md:grid-cols-12 gap-10 py-16">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <img src={BRAND.logo} alt="" className="h-12 w-12 rounded-full bg-primary-foreground/10 p-1" />
              <div>
                <div className="font-display text-lg font-semibold">Vegan Danish</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-primary-foreground/60">Bakery Foods™</div>
              </div>
            </div>
            <p className="mt-5 text-primary-foreground/70 leading-relaxed text-sm">
              Premium plant-based breakfast — engineered for grab-and-go campus dining,
              independent cafés and forward-thinking foodservice operators.
            </p>
            <div className="flex gap-3 mt-6">
              {[
                { Icon: Instagram, href: 'https://www.instagram.com/vegandanishbakerytoronto/?hl=en', label: 'Instagram' },
                { Icon: Linkedin, href: '#', label: 'LinkedIn' },
                { Icon: Mail, href: `mailto:${BRAND.email}`, label: 'Email' }
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith('http') ? '_blank' : undefined}
                  rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  aria-label={label}
                  className="h-10 w-10 rounded-full bg-primary-foreground/10 hover:bg-accent hover:text-accent-foreground transition-colors grid place-items-center"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.18em] text-primary-foreground/50 mb-4">Product line</div>
            <ul className="space-y-2.5 text-sm">
              {PRODUCTS.slice(0, 8).map(p => (
                <li key={p.slug}><Link to={`/products/${p.slug}`} className="text-primary-foreground/80 hover:text-accent transition-colors">{p.name}</Link></li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.18em] text-primary-foreground/50 mb-4">Company</div>
            <ul className="space-y-2.5 text-sm text-primary-foreground/80">
              <li><Link to="/craft" className="hover:text-accent">Our Craft</Link></li>
              <li><Link to="/campus" className="hover:text-accent">Campus Program</Link></li>
              <li><Link to="/sustainability" className="hover:text-accent">Sustainability</Link></li>
              <li><Link to="/wholesale" className="hover:text-accent">Wholesale</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.18em] text-primary-foreground/50 mb-4">Visit the bakery</div>
            <ul className="space-y-3 text-sm text-primary-foreground/80">
              <li className="flex items-start gap-2.5"><MapPin className="h-4 w-4 mt-0.5 text-accent" />{BRAND.address}</li>
              <li className="flex items-center gap-2.5"><Phone className="h-4 w-4 text-accent" />{BRAND.phone}</li>
              <li className="flex items-center gap-2.5"><Mail className="h-4 w-4 text-accent" />{BRAND.email}</li>
            </ul>
            <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-foreground/10 text-xs">
              <Leaf className="h-3.5 w-3.5 text-accent" /> 100% plant-based, every batch
            </div>
          </div>
        </div>

        <div className="py-6 border-t border-primary-foreground/15 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-primary-foreground/60">
          <div>© {new Date().getFullYear()} Vegan Danish Bakery Foods™. Baked with intention in Ontario, Canada.</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-accent">Privacy</a>
            <a href="#" className="hover:text-accent">Terms</a>
            <a href="#" className="hover:text-accent">Allergen Sheets</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
